import React, { useMemo, useState, useEffect } from 'react';
import {
  BarChart, LineChart, PieChart, AreaChart, ScatterChart, ComposedChart,
  ResponsiveContainer, XAxis, YAxis, Legend, Tooltip, Bar, Line, Pie, Area, Scatter, Cell, CartesianGrid
} from 'recharts';
import { Box, Typography, FormControl, FormGroup, FormControlLabel, Checkbox, Select, MenuItem } from '@mui/material';

// Chart component mappings
const chartComponents = {
  BarChart, LineChart, PieChart, AreaChart, ScatterChart, ComposedChart
};

const seriesComponents = {
  bar: Bar, line: Line, pie: Pie, area: Area, scatter: Scatter
};

// Default configurations
const DEFAULT_CONFIG = {
  MARGINS: {
    pie: { top: 20, right: 100, bottom: 30, left: 30 },
    cartesian: { top: 80, right: 30, bottom: 90, left: 60 } // Increased top margin for legend
  },
  COLORS: ['#4B5EAA', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD'],
  SHAPES: { 'Minimum Price': 'triangle', 'Average Price': 'circle', 'Maximum Price': 'diamond' },
  METRIC_PRIORITY: ['TotalSalesValue', 'GrowthPercentage', 'SharePercentage', 'TotalSalesVolume', 'SalesValueGrowthPct', 'SalesVolumeGrowthPct'],
  MONTH_NAMES: {
    'en-US': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    // Add more locales as needed, e.g., 'fr-FR': ['Janv', 'Févr', ...]
  }
};

// Formatter function with locale support
const formatValue = (value, formatter, unit = '', dataKey = '', defaultToMillions = true, locale = 'en-US') => {
  if (value == null || isNaN(value)) return `${value || ''}${unit}`;
  const num = Number(value);
  const adjustedUnit = unit === '$' && dataKey.includes('TotalSalesVolume') ? 'Mkg' : unit;
  const isDollarUnit = unit === '$' && !dataKey.includes('TotalSalesValue') && !dataKey.includes('TotalSalesVolume');

  const formatters = {
    monthName: () => DEFAULT_CONFIG.MONTH_NAMES[locale]?.[num - 1] || String(value),
    slice: () => `${String(value).slice(0, 4)}-${String(value).slice(4)}`,
    '$,.0f': () => defaultToMillions && !isDollarUnit
      ? Math.abs(num) >= 1000000 ? `${(num / 1e6).toFixed(1)}M${adjustedUnit}`
      : Math.abs(num) >= 1000 ? `${(num / 1e6).toFixed(3)}M${adjustedUnit}`
      : `${num.toFixed(0)}${adjustedUnit}`
      : `$${num.toLocaleString(locale, { maximumFractionDigits: 0 })}`,
    toFixed: (precision) => defaultToMillions && adjustedUnit !== '%' && !isDollarUnit
      ? Math.abs(num) >= 1000000 ? `${(num / 1e6).toFixed(1)}M${adjustedUnit}`
      : Math.abs(num) >= 1000 ? `${(num / 1e6).toFixed(3)}M${adjustedUnit}`
      : `${num.toFixed(precision)}${adjustedUnit}`
      : `${num.toFixed(precision)}${adjustedUnit}`,
    autoCurrencyOrNumber: () => isDollarUnit || adjustedUnit === '$'
      ? `$${num.toLocaleString(locale, { maximumFractionDigits: 2 })}`
      : num.toLocaleString(locale, { maximumFractionDigits: 0 }),
    toLocaleString: () => defaultToMillions && adjustedUnit !== '%' && !isDollarUnit
      ? Math.abs(num) >= 1000000 ? `${(num / 1e6).toFixed(1)}M${adjustedUnit}`
      : Math.abs(num) >= 1000 ? `${(num / 1e6).toFixed(3)}M${adjustedUnit}`
      : `${num.toLocaleString(locale, { maximumFractionDigits: 0 })}${adjustedUnit}`
      : `${num.toLocaleString(locale)}${adjustedUnit}`
  };

  if (typeof formatter === 'string') {
    if (formatter.includes('{value}')) return formatter.replace('{value}', num.toLocaleString(locale, { maximumFractionDigits: 2 }));
    if (formatter.includes('toFixed')) {
      const precision = parseInt(formatter.match(/toFixed\((\d+)\)/)?.[1] || 0);
      return formatters.toFixed(precision);
    }
    if (formatter.includes("['Jan','Feb'")) return formatters.monthName();
    return formatters[formatter]?.() || (adjustedUnit === '%' ? `${num.toFixed(1)}%` : formatters.toLocaleString());
  }
  return formatters.toLocaleString();
};

// Custom Tooltip Components
const CustomPieTooltip = ({ active, payload, valueFormatter, locale }) => active && payload?.length ? (
  <div style={{ backgroundColor: '#ffffff', padding: '8px 12px', border: '1px solid #cccccc', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
    <p style={{ margin: 0, color: payload[0].payload.fill || '#000000', fontWeight: 'bold' }}>{payload[0].name}</p>
    <p style={{ margin: '4px 0 0 0', color: '#666666' }}><span style={{ fontWeight: 'bold' }}>Value: </span>{valueFormatter(payload[0].value, payload[0].payload.unit)}</p>
    {typeof payload[0].percent === 'number' && (
      <p style={{ margin: '4px 0 0 0', color: '#666666' }}><span style={{ fontWeight: 'bold' }}>Percent: </span>{(payload[0].percent * 100).toFixed(1)}%</p>
    )}
    {['year', 'month', 'manufacturer', 'brand', 'segment', 'CalYear', 'CalMonth']
      .filter(field => payload[0].payload[field])
      .map(field => (
        <p key={field} style={{ margin: '4px 0 0 0', color: '#666666' }}>
          <span style={{ fontWeight: 'bold' }}>{field.charAt(0).toUpperCase() + field.slice(1)}: </span>{payload[0].payload[field]}
        </p>
      ))}
  </div>
) : null;

const CustomCartesianTooltip = ({ active, payload, label, chartData, filteredSeries, locale }) => {
  if (!active || !payload?.length) return null;
  const xAxisDataKey = chartData.yAxis?.dataKey || chartData.xAxis?.dataKey;
  const fields = [...new Set([
    'year', 'month', 'manufacturer', 'brand', 'segment', 'CalYear', 'CalMonth', 'ManufacturerName', 'BrandName',
    ...(chartData.filters?.map(f => f.column).filter(c => c !== xAxisDataKey) || [])
  ])].filter(field => payload[0].payload[field] && field !== xAxisDataKey);

  return (
    <div style={{ backgroundColor: '#ffffff', padding: '8px 12px', border: '1px solid #cccccc', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <p style={{ margin: 0, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px', marginBottom: '5px' }}>
        {chartData.xAxis?.label || chartData.yAxis?.label}: {formatValue(label, chartData.xAxis?.tickFormatter, '', '', false, locale)}
      </p>
      {payload.map((entry, index) => entry.value && (
        <p key={`item-${index}`} style={{ margin: '4px 0 0 0', color: entry.color }}>
          {entry.name}: <span style={{ fontWeight: 'bold' }}>
            {formatValue(
              entry.value,
              filteredSeries.find(s => s.name === entry.name || s.dataKey === entry.name)?.formatter || chartData.tooltip?.formatter,
              filteredSeries.find(s => s.name === entry.name || s.dataKey === entry.name)?.unit || chartData.yAxis?.unit || '',
              filteredSeries.find(s => s.name === entry.name || s.dataKey === entry.name)?.dataKey || '',
              true,
              locale
            )}
          </span>
        </p>
      ))}
      {fields.map(field => (
        <p key={field} style={{ margin: '4px 0 0 0', color: '#666666' }}>
          <span style={{ fontWeight: 'bold' }}>{field.charAt(0).toUpperCase() + field.slice(1)}: </span>{payload[0].payload[field]}
        </p>
      ))}
    </div>
  );
};

// Main Chart Component
const ChartContent = ({ chartData, locale = 'en-US' }) => {
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [selectedSecondaryFilterValues, setSelectedSecondaryFilterValues] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('');

  // Initialize states
  useEffect(() => {
    const initialFilters = chartData?.filters?.reduce((acc, f) => ({ ...acc, [f.column]: [] }), {}) || {};
    setSelectedFilterValues(initialFilters);
    setSelectedSecondaryFilterValues([]);
    const metrics = [...new Set(chartData?.series?.map(s => s.dataKey) || [])];
    setSelectedMetric(metrics.find(m => DEFAULT_CONFIG.METRIC_PRIORITY.includes(m)) || metrics[0] || DEFAULT_CONFIG.METRIC_PRIORITY[0]);
  }, [chartData]);

  // Validate chart data
  if (!chartData || !chartData.type || !chartData.data || !chartData.series) {
    return <Box sx={{ textAlign: 'center', py: 2 }}><Typography color="error">Invalid or missing chart data</Typography></Box>;
  }
  if (chartData.error) {
    return <Box sx={{ textAlign: 'center', py: 2 }}><Typography color="error">{chartData.error}</Typography></Box>;
  }
  const ChartComponent = chartComponents[chartData.type];
  if (!ChartComponent) {
    return <Box sx={{ textAlign: 'center', py: 2 }}><Typography color="error">Unsupported chart type: {chartData.type}</Typography></Box>;
  }

  // Chart type flags
  const isPieChart = chartData.type === 'PieChart';
  const isLineChart = chartData.type === 'LineChart';
  const isBarChart = chartData.type === 'BarChart';
  const isAreaChart = chartData.type === 'AreaChart';
  const isScatterChart = chartData.type === 'ScatterChart';
  const isComposedChart = chartData.type === 'ComposedChart';
  const isVerticalLayout = chartData.style?.layout === 'vertical';
  const filterAppliesToData = chartData.filters?.every(f => chartData.data.some(item => f.column in item));
  const filterAppliesToSeries = chartData.filters?.some(f => chartData.series.some(s => f.values.includes(parseInt(s.dataKey)) || f.values.includes(s.dataKey)));
  const secondaryFilterAppliesToData = chartData.secondaryFilter && chartData.data.some(item => chartData.secondaryFilter.column in item);
  const hasPredefinedSeries = isLineChart && chartData.series.length > 1 && new Set(chartData.series.map(s => s.dataKey)).size === 1;

  // Transform data for LineChart
  const transformedData = useMemo(() => {
    if (!isLineChart || !filterAppliesToData || !chartData.filters?.length || hasPredefinedSeries) return chartData.data;
    const filterKey = chartData.filters[0].column;
    const filteredValues = selectedFilterValues[filterKey]?.length > 0 ? selectedFilterValues[filterKey] : chartData.filters[0].values;
    const periods = [...new Set(chartData.data.map(item => item[chartData.xAxis.dataKey]))].sort((a, b) => a - b);

    return periods.map(period => {
      const entries = chartData.data.filter(item =>
        item[chartData.xAxis.dataKey] === period &&
        filteredValues.includes(item[filterKey]) &&
        (selectedSecondaryFilterValues.length === 0 || selectedSecondaryFilterValues.includes(item[chartData.secondaryFilter?.column]))
      );
      if (!entries.length) return null;
      const newEntry = { [chartData.xAxis.dataKey]: period };
      entries.forEach(entry => {
        newEntry[`${selectedMetric}_${entry[filterKey]}`] = entry[selectedMetric];
        ['year', 'month', 'manufacturer', 'brand', 'segment', 'CalYear', 'CalMonth', 'BrandName', 'ManufacturerName', 'Category']
          .forEach(field => entry[field] && (newEntry[field] = entry[field]));
      });
      return newEntry;
    }).filter(Boolean);
  }, [isLineChart, filterAppliesToData, chartData.data, chartData.xAxis, chartData.filters, chartData.secondaryFilter, selectedFilterValues, selectedSecondaryFilterValues, selectedMetric, hasPredefinedSeries]);

  // Adjust series for LineChart
  const adjustedSeries = useMemo(() => {
    if (!isLineChart || !filterAppliesToData || !chartData.filters?.length || hasPredefinedSeries) return chartData.series;
    const filterKey = chartData.filters[0].column;
    const filteredValues = selectedFilterValues[filterKey]?.length > 0 ? selectedFilterValues[filterKey] : chartData.filters[0].values;
    const originalSeries = chartData.series.find(item => item.dataKey === selectedMetric) || chartData.series[0] || {};

    return filteredValues.map((value, index) => ({
      dataKey: `${selectedMetric}_${value}`,
      name: formatValue(value, chartData.xAxis?.tickFormatter, '', '', false, locale),
      stroke: chartData.style?.colorScale?.[index % chartData.style.colorScale.length] || DEFAULT_CONFIG.COLORS[index % DEFAULT_CONFIG.lengthS.length],
      fill: chartData.style?.colorScale?.[index % chartData.style.colorScale.length] || DEFAULT_CONFIG.COLORS[index % DEFAULT_CONFIG.COLORS.length],
      unit: selectedMetric === 'TotalSalesVolume' ? 'Mkg' : originalSeries.unit || (selectedMetric.includes('GrowthPct') ? '%' : '$'),
      type: 'line',
      strokeWidth: originalSeries.strokeWidth || 3,
      dot: originalSeries.dot || { r: 4, strokeWidth: 2 },
      activeDot: originalSeries.activeDot || { r: 6, strokeWidth: 2 },
      isAnimationActive: originalSeries.isAnimationActive ?? true,
      connectNulls: true,
      formatter: originalSeries.formatter || '.2f'
    }));
  }, [isLineChart, filterAppliesToData, chartData.series, chartData.filters, chartData.style, selectedFilterValues, selectedMetric, chartData.xAxis, hasPredefinedSeries, locale]);

  // Compute series totals
  const seriesTotals = useMemo(() => {
    if (!chartData.filters || !chartData.data || !chartData.series) return {};
    const dataToUse = isLineChart && filterAppliesToData && !hasPredefinedSeries ? transformedData : chartData.data;
    const seriesToUse = isLineChart && filterAppliesToData && !hasPredefinedSeries ? adjustedSeries : chartData.series;

    return seriesToUse.reduce((totals, series) => {
      totals[series.dataKey] = dataToUse.reduce((sum, item) => {
        const value = hasPredefinedSeries
          ? (item[series.dataKey] && item[chartData.filters?.[0]?.column] === series.name ? item[series.dataKey] : 0)
          : item[series.dataKey];
        return sum + (typeof value === 'number' && !isNaN(value) ? value : 0);
      }, 0);
      return totals;
    }, {});
  }, [chartData.data, chartData.series, chartData.filters, isLineChart, filterAppliesToData, transformedData, adjustedSeries, hasPredefinedSeries]);

  // Compute filter values
  const topFilterValues = useMemo(() => {
    if (!chartData.filters) return {};
    return chartData.filters.reduce((result, filter) => {
      if (filterAppliesToData) {
        const valueMap = chartData.data.reduce((acc, item) => {
          const filterValue = item[filter.column];
          if (filter.values.includes(filterValue)) acc[filterValue] = (acc[filterValue] || 0) + (item[selectedMetric] || 0);
          return acc;
        }, {});
        result[filter.column] = filter.values.slice().sort((a, b) => (valueMap[b] || 0) - (valueMap[a] || 0)).slice(0, 10);
      } else if (filterAppliesToSeries) {
        result[filter.column] = filter.values.slice().sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0)).slice(0, 10);
      } else {
        result[filter.column] = [];
      }
      return result;
    }, {});
  }, [chartData.data, chartData.filters, filterAppliesToData, filterAppliesToSeries, seriesTotals, selectedMetric]);

  const topSecondaryFilterValues = useMemo(() => {
    if (!chartData.secondaryFilter?.values) return [];
    return secondaryFilterAppliesToData ? chartData.secondaryFilter.values.slice().sort((a, b) => b - a) : [];
  }, [chartData.secondaryFilter, secondaryFilterAppliesToData]);

  // Filter data
  const filteredData = useMemo(() => {
    let data = isLineChart && filterAppliesToData && !hasPredefinedSeries ? transformedData : chartData.data;
    if (isBarChart && (!chartData.filters || Object.values(selectedFilterValues).every(arr => !arr.length) || !filterAppliesToData)) {
      data = [...data].sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0)).slice(0, 10);
    }
    if (chartData.filters && filterAppliesToData && (!isLineChart || hasPredefinedSeries)) {
      data = data.filter(item => chartData.filters.every(f => (selectedFilterValues[f.column]?.length > 0 ? selectedFilterValues[f.column] : f.values).includes(item[f.column])));
    }
    if (chartData.secondaryFilter && selectedSecondaryFilterValues.length > 0 && secondaryFilterAppliesToData && (!isLineChart || hasPredefinedSeries)) {
      data = data.filter(item => selectedSecondaryFilterValues.includes(item[chartData.secondaryFilter.column]));
    }
    return data;
  }, [chartData.data, chartData.filters, chartData.secondaryFilter, selectedFilterValues, selectedSecondaryFilterValues, isLineChart, isBarChart, transformedData, selectedMetric, secondaryFilterAppliesToData, hasPredefinedSeries]);

  // Filter series
  const filteredSeries = useMemo(() => {
    const seriesToUse = isLineChart && filterAppliesToData && !hasPredefinedSeries ? adjustedSeries : chartData.series;
    return seriesToUse.filter(series => {
      const dataToCheck = isLineChart && filterAppliesToData && !hasPredefinedSeries ? filteredData : chartData.data;
      const hasData = dataToCheck.some(item => {
        const value = hasPredefinedSeries
          ? (item[series.dataKey] && item[chartData.filters?.[0]?.column] === series.name ? item[series.dataKey] : null)
          : item[series.dataKey];
        return value != null && !isNaN(value);
      });
      return hasData && (!filterAppliesToSeries || chartData.filters.some(f => selectedFilterValues[f.column]?.includes(parseInt(series.dataKey)) || selectedFilterValues[f.column]?.includes(series.dataKey)));
    });
  }, [chartData.series, chartData.data, filteredData, selectedFilterValues, filterAppliesToData, filterAppliesToSeries, isLineChart, adjustedSeries, hasPredefinedSeries]);

  // Compute margins
  const margins = useMemo(() => ({
    top: (isLineChart || isBarChart || isAreaChart || isScatterChart || isComposedChart ? chartData.margin?.top + 40 : chartData.margin?.top) || DEFAULT_CONFIG.MARGINS[isPieChart ? 'pie' : 'cartesian'].top,
    right: (isPieChart ? chartData.margin?.right + 60 : chartData.margin?.right) || DEFAULT_CONFIG.MARGINS[isPieChart ? 'pie' : 'cartesian'].right,
    bottom: (isPieChart ? chartData.margin?.bottom : chartData.margin?.bottom + (filteredData.length > 8 ? 60 : 40)) || DEFAULT_CONFIG.MARGINS[isPieChart ? 'pie' : 'cartesian'].bottom,
    left: (isPieChart ? chartData.margin?.left : chartData.margin?.left + 40) || DEFAULT_CONFIG.MARGINS[isPieChart ? 'pie' : 'cartesian'].left
  }), [chartData.margin, isPieChart, isLineChart, isBarChart, isAreaChart, isScatterChart, isComposedChart, filteredData.length]);

  // Compute legend configuration
  const legendConfig = useMemo(() => {
    if (!chartData.legend) return null;
    const defaultIcon = isLineChart ? 'line' : isScatterChart ? 'circle' : isBarChart ? 'rect' : isPieChart ? 'circle' : isComposedChart ? 'rect' : 'line';
    return {
      layout: chartData.legend.layout || (isPieChart ? 'vertical' : 'horizontal'),
      align: chartData.legend.align || (isPieChart ? 'right' : 'center'),
      verticalAlign: 'top', // Explicitly set to top for all charts
      iconType: chartData.legend.iconType || defaultIcon,
      wrapperStyle: isPieChart && chartData.legend.layout === 'vertical'
        ? { right: 10, top: 10, maxWidth: '30%' } // Adjusted for PieChart vertical legend
        : { paddingBottom: 10, marginTop: 0 } // Minimal margin for top placement
    };
  }, [chartData.legend, isPieChart, isLineChart, isScatterChart, isBarChart, isComposedChart]);

  // Compute max value for vertical bar charts
  const maxValue = useMemo(() => isBarChart && isVerticalLayout
    ? Math.max(...filteredData.map(item => Math.max(item[selectedMetric] || 0, item.TotalSalesVolume || 0))) * 1.1
    : null, [filteredData, isBarChart, isVerticalLayout, selectedMetric]);

  // Compute legend payload
  const legendPayload = useMemo(() => {
    if (!chartData.legend || !legendConfig || isPieChart) return null;
    const metricSuffix = chartData.yAxis?.label
      ? chartData.yAxis.label.toLowerCase().replace(/^total\s*/, '').replace(/\s*\([\$\%\w\s-]*\)$/, '').replace(/\s*[\$\%]$/, '').trim()
        .replace(/\b\w/g, char => char.toUpperCase()) : '';
    return filteredSeries.map((series, index) => {
      const baseName = String(series.name || series.dataKey || 'Unknown');
      const finalName = metricSuffix && !baseName.toLowerCase().includes(metricSuffix.toLowerCase()) && (chartData.yAxis?.unit === '$' || chartData.yAxis?.unit === '%') && !isLineChart
        ? `${baseName} - ${metricSuffix}`
        : baseName;
      const color = series.fill || series.stroke || chartData.style?.colorScale?.[index % chartData.style.colorScale.length] || DEFAULT_CONFIG.COLORS[index % DEFAULT_CONFIG.COLORS.length];
      const iconType = isComposedChart && series.type
        ? series.type === 'line' ? 'line' : series.type === 'bar' ? 'rect' : series.type === 'area' ? 'rect' : series.type === 'scatter' ? 'circle' : legendConfig.iconType
        : isScatterChart ? DEFAULT_CONFIG.SHAPES[series.dataKey] || legendConfig.iconType : legendConfig.iconType;
      return { value: finalName, type: iconType, id: baseName, color };
    });
  }, [chartData.legend, chartData.yAxis, chartData.style, filteredSeries, isPieChart, isLineChart, isScatterChart, isComposedChart, legendConfig]);

  // Pie chart name key
  const nameKeyForPie = useMemo(() => {
    if (!isPieChart || !chartData.data?.length || !chartData.series?.length) return 'name';
    const seriesDataKey = chartData.series[0]?.dataKey;
    return seriesDataKey && chartData.data[0]
      ? Object.keys(chartData.data[0]).find(key => key !== seriesDataKey && key !== 'payload' && key !== 'parentView') || 'name'
      : 'name';
  }, [isPieChart, chartData.data, chartData.series]);

  // Handlers
  const handleFilterChange = (filterColumn, value) => setSelectedFilterValues(prev => ({
    ...prev,
    [filterColumn]: prev[filterColumn]?.includes(value) ? prev[filterColumn].filter(v => v !== value) : [...prev[filterColumn], value]
  }));

  const handleSecondaryFilterChange = value => setSelectedSecondaryFilterValues(prev =>
    prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
  );

  const handleMetricChange = e => setSelectedMetric(e.target.value);

  return (
    <Box sx={{ my: 2, maxWidth: '1200px', mx: 'auto' }}>
      {chartData.title && <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold', color: '#022A3E' }}>{chartData.title}</Typography>}
      {chartData.description && <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: '#5D5D5D' }}>{chartData.description}</Typography>}
      {(chartData.filters || (chartData.secondaryFilter && chartData.secondaryFilter.column !== 'CalYearMonth') || isLineChart) && (
        <Box sx={{ mb: 2, px: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {isLineChart && (
            <FormControl sx={{ minWidth: 120 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Metric</Typography>
              <Select value={selectedMetric} onChange={handleMetricChange} size="small">
                {[...new Set(chartData.series?.map(s => s.dataKey) || [])].map(metric => (
                  <MenuItem key={metric} value={metric}>{metric.replace(/([A-Z])/g, ' $1').trim()}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {chartData.filters?.length > 0 && (filterAppliesToData || filterAppliesToSeries) && chartData.filters.map(filter => (
            <FormControl key={filter.column} component="fieldset">
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{filter.label || `Filter by ${filter.column}`}</Typography>
              <FormGroup row sx={{ flexWrap: 'wrap' }}>
                {(topFilterValues[filter.column] || []).map(value => (
                  <FormControlLabel
                    key={value}
                    control={<Checkbox checked={selectedFilterValues[filter.column]?.includes(value) || false} onChange={() => handleFilterChange(filter.column, value)} name={String(value)} />}
                    label={formatValue(value, chartData.xAxis?.tickFormatter, '', '', false, locale)}
                  />
                ))}
              </FormGroup>
            </FormControl>
          ))}
          {chartData.secondaryFilter && chartData.secondaryFilter.column !== 'CalYearMonth' && chartData.secondaryFilter.values?.length > 0 && secondaryFilterAppliesToData && (
            <FormControl component="fieldset">
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{chartData.secondaryFilter.label || `Filter by ${chartData.secondaryFilter.column}`}</Typography>
              <FormGroup row sx={{ flexWrap: 'wrap' }}>
                {topSecondaryFilterValues.map(value => (
                  <FormControlLabel
                    key={value}
                    control={<Checkbox checked={selectedSecondaryFilterValues.includes(value)} onChange={() => handleSecondaryFilterChange(value)} name={String(value)} />}
                    label={formatValue(value, chartData.secondaryFilter?.formatter, '', '', false, locale)}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}
        </Box>
      )}
      <ResponsiveContainer
        width={chartData.responsive ? '100%' : chartData.width ? chartData.width * 1.5 : 900}
        height={chartData.height ? chartData.height * 1.5 : 600}
        aspect={chartData.aspectRatio || 1.5}
      >
        <ChartComponent data={filteredData} margin={margins} layout={isVerticalLayout ? 'vertical' : 'horizontal'} barGap={isBarChart || isComposedChart ? chartData.style?.barGap : undefined}>
          {chartData.legend && legendConfig && <Legend {...legendConfig} payload={legendPayload || undefined} />}
          {isPieChart || (chartData.style?.grid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />)}
          {isPieChart || (chartData.xAxis && (
            <XAxis
              dataKey={isVerticalLayout ? undefined : chartData.xAxis.dataKey}
              label={isVerticalLayout && chartData.yAxis?.label ? { value: chartData.yAxis.label, position: 'bottom', offset: 50, fill: '#022A3E' } : chartData.xAxis.label ? { value: chartData.xAxis.label, position: 'bottom', offset: 50, fill: '#022A3E' } : undefined}
              type={isVerticalLayout ? 'number' : chartData.xAxis.type || 'category'}
              unit={chartData.xAxis.unit}
              domain={isVerticalLayout ? [0, maxValue || 'auto'] : chartData.xAxis.domain || ['dataMin', 'dataMax']}
              tickCount={chartData.xAxis.tickCount || 7}
              allowDecimals={chartData.xAxis.allowDecimals ?? true}
              padding={chartData.xAxis.padding || { left: 10, right: 10 }}
              tickFormatter={value => formatValue(value, chartData.xAxis.tickFormatter, chartData.xAxis.unit || '', '', false, locale)}
              stroke="#5D5D5D"
              tick={{ fill: '#5D5D5D', fontSize: 12, angle: -45, textAnchor: 'end' }}
            />
          ))}
          {isPieChart || (chartData.yAxis && (
            <YAxis
              dataKey={isVerticalLayout ? chartData.xAxis.dataKey : chartData.yAxis.dataKey}
              label={isVerticalLayout && chartData.xAxis.label
                ? { value: chartData.xAxis.label, angle: -90, position: 'insideLeft', dx: -80, dy: 70, fill: '#022A3E', textAnchor: 'middle' }
                : chartData.yAxis.label
                ? { value: chartData.yAxis.label, angle: -90, position: 'insideLeft', dx: -40, dy: 120, fill: '#022A3E', textAnchor: 'middle' }
                : undefined}
              type={isVerticalLayout ? 'category' : chartData.yAxis.type || 'number'}
              domain={isVerticalLayout ? undefined : chartData.yAxis.domain || ['auto', 'auto']}
              tickCount={chartData.yAxis.tickCount || 5}
              allowDecimals={chartData.yAxis.allowDecimals ?? true}
              padding={chartData.yAxis.padding || { top: 0, bottom: 0 }}
              tickFormatter={value => formatValue(value, chartData.series.find(s => s.dataKey === selectedMetric)?.formatter || chartData.yAxis.tickFormatter, chartData.series.find(s => s.dataKey === selectedMetric)?.unit || chartData.yAxis.unit || '', chartData.yAxis.dataKey || '', true, locale)}
              stroke="#5D5D5D"
              tick={{ fill: '#5D5D5D', fontSize: 12 }}
            />
          ))}
          {isPieChart ? (
            <Tooltip content={<CustomPieTooltip valueFormatter={(value, unit) => formatValue(value, filteredSeries[0]?.formatter || chartData.tooltip?.formatter, unit || filteredSeries[0]?.unit || '', filteredSeries[0]?.dataKey || '', true, locale)} locale={locale} />} />
          ) : (
            chartData.tooltip && (
              <Tooltip
                separator={chartData.tooltip.separator || ': '}
                contentStyle={chartData.tooltip.contentStyle || { backgroundColor: '#ffffff', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                cursor={chartData.tooltip.cursor ?? { fill: 'rgba(75,94,170,0.1)' }}
                shared={chartData.tooltip.shared ?? true}
                content={<CustomCartesianTooltip chartData={chartData} filteredSeries={filteredSeries} locale={locale} />}
                labelFormatter={value => formatValue(value, chartData.tooltip.labelFormatter, chartData.xAxis?.unit || '', '', false, locale)}
              />
            )
          )}
          {filteredSeries.map((series, index) => {
            const seriesType = series.type || (isScatterChart ? 'scatter' : isAreaChart ? 'area' : isLineChart ? 'line' : isBarChart ? 'bar' : isComposedChart ? 'bar' : chartData.type.toLowerCase().replace('chart', ''));
            const SeriesComponent = seriesComponents[seriesType] || Bar;
            const color = series.fill || series.stroke || chartData.style?.colorScale?.[index % chartData.style.colorScale.length] || DEFAULT_CONFIG.COLORS[index % DEFAULT_CONFIG.COLORS.length];

            if (isPieChart) {
              return (
                <Pie
                  key={`${series.dataKey}-${index}`}
                  data={filteredData}
                  dataKey={series.dataKey}
                  nameKey={nameKeyForPie}
                  cx="50%"
                  cy="50%"
                  innerRadius={chartData.style?.innerRadius || 0}
                  outerRadius={chartData.style?.outerRadius || '70%'}
                  paddingAngle={chartData.style?.paddingAngle ?? 2}
                  labelLine={false}
                  isAnimationActive={series.isAnimationActive ?? chartData.style?.animation ?? true}
                >
                  {filteredData.map((entry, idx) => (
                    <Cell
                      key={`cell-${idx}`}
                      fill={chartData.style?.colorScale?.[idx % chartData.style.colorScale.length] || DEFAULT_CONFIG.COLORS[idx % DEFAULT_CONFIG.COLORS.length]}
                      fillOpacity={series.fillOpacity || 1}
                      stroke={chartData.style?.cellStroke || '#ffffff'}
                      strokeWidth={chartData.style?.cellStrokeWidth ?? 2}
                    />
                  ))}
                </Pie>
              );
            }
            return (
              <SeriesComponent
                key={`${series.dataKey}-${index}`}
                dataKey={series.dataKey}
                name={series.name || series.dataKey}
                fill={color}
                stroke={color}
                stackId={chartData.style?.stacked ? series.stackId || 'stack' : series.stackId}
                fillOpacity={series.fillOpacity || (isAreaChart ? 0.6 : 0.8)}
                strokeWidth={isLineChart || isAreaChart || (isComposedChart && seriesType === 'line') ? series.strokeWidth || 2.5 : series.strokeWidth || 1}
                dot={(isLineChart || isAreaChart || (isComposedChart && seriesType === 'line')) ? (series.dot || { r: 3 }) : undefined}
                activeDot={(isLineChart || isAreaChart || (isComposedChart && seriesType === 'line')) ? (series.activeDot || { r: 5 }) : undefined}
                isAnimationActive={series.isAnimationActive ?? chartData.style?.animation ?? true}
                type={(isLineChart || isAreaChart || (isComposedChart && seriesType === 'line')) ? (series.lineType || 'monotone') : undefined}
                connectNulls={(isLineChart || isAreaChart || (isComposedChart && (seriesType === 'line' || seriesType === 'area'))) ? true : undefined}
                barSize={isBarChart || isComposedChart ? chartData.style?.barSize : undefined}
                maxBarSize={isBarChart || isComposedChart ? chartData?.style?.maxBarSize : undefined}
                shape={isScatterChart ? DEFAULT_CONFIG.SHAPES[series.dataKey] || 'circle' : undefined}
               />
            );
          })}
        </ChartComponent>
      </ResponsiveContainer>
    </Box>
  );
};

export default ChartContent;